/**
 * Foreground network awareness for background work. The EMR background sync pulls
 * hundreds of pages; on slow connections those requests compete with whatever the
 * user is actually doing. Axios interceptors count every in-flight request EXCEPT
 * ones marked as background (X-Background-Sync header), and background workers call
 * waitForForegroundNetworkIdle() before each of their requests — so user-initiated
 * traffic always wins and sync fills the gaps.
 */

import axios from 'axios'

export const BACKGROUND_REQUEST_HEADER = 'X-Background-Sync'

let installed = false
let activeForegroundRequests = 0
let lastForegroundFinishedAt = 0

function isBackgroundConfig(config) {
  return Boolean(config?.headers?.[BACKGROUND_REQUEST_HEADER])
}

export function installAxiosNetworkTracking() {
  if (installed) {
    return
  }
  installed = true
  axios.interceptors.request.use((config) => {
    if (!isBackgroundConfig(config)) {
      config.__foregroundTracked = true
      activeForegroundRequests += 1
    }
    return config
  })
  const settle = (config) => {
    if (config?.__foregroundTracked) {
      config.__foregroundTracked = false
      activeForegroundRequests = Math.max(0, activeForegroundRequests - 1)
      lastForegroundFinishedAt = Date.now()
    }
  }
  axios.interceptors.response.use(
    (response) => {
      settle(response?.config)
      return response
    },
    (error) => {
      settle(error?.config)
      return Promise.reject(error)
    },
  )
}

export function isForegroundNetworkBusy(idleMs = 300) {
  return (
    activeForegroundRequests > 0 || Date.now() - lastForegroundFinishedAt < idleMs
  )
}

/**
 * Resolves once no foreground request has been in flight for idleMs. maxWaitMs is a
 * starvation guard: pages that poll continuously (e.g. chamber screens) must not
 * stall the sync forever — after the cap the background request proceeds anyway.
 */
export async function waitForForegroundNetworkIdle({ idleMs = 300, maxWaitMs = 12000 } = {}) {
  const startedAt = Date.now()
  while (isForegroundNetworkBusy(idleMs)) {
    if (Date.now() - startedAt >= maxWaitMs) {
      return
    }
    await new Promise((resolve) => setTimeout(resolve, 150))
  }
}
